import PropTypes from 'prop-types';
import React from 'react';

import { useTranslation } from 'react-i18next';

import { Search, Button, Modal, Table } from '@weezevent/nacre';

function SelectableEvent({ event, selectedEvents, onChange = () => {}, label }) {
    return (
        <Table.Tr active={selectedEvents.includes(event.id)} onSelected={onChange} key={event.id}>
            <Table.Td>{label}</Table.Td>
        </Table.Tr>
    );
}

SelectableEvent.propTypes = {
    event: PropTypes.object.isRequired,
    selectedEvents: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired
};

export default function EventsPickerModal({
    open = false,
    events = [],
    onSubmit = () => {},
    close = () => {},
    formatLabel = event => {
        return event.name;
    },
    isSubmitting = false
}) {
    const { t } = useTranslation();

    const [selectedEvents, setSelectedEvents] = React.useState([]);
    const [filteredEvents, setFilteredEvents] = React.useState(events);
    const [allSelected, setAllSelected] = React.useState(false);

    const selectEvent = React.useCallback(
        event => {
            let newSelectedEvents = [...selectedEvents, event.id];
            setSelectedEvents(newSelectedEvents);
            //Case last checkbox has been checked
            if (newSelectedEvents.length === filteredEvents.length) {
                setAllSelected(true);
            }
        },
        [selectedEvents, filteredEvents]
    );

    const deselectEvent = React.useCallback(
        event => {
            if (allSelected) {
                setAllSelected(false);
            }
            let newSelectedEvents = [...selectedEvents];
            let index = newSelectedEvents.indexOf(event.id);
            newSelectedEvents.splice(index, 1);
            setSelectedEvents(newSelectedEvents);
        },
        [selectedEvents, allSelected]
    );

    const filterEvents = React.useCallback(
        value => {
            let words = value.split(' ');

            let filtered = events.filter(event => {
                let regexpWords = words.map(word => new RegExp(word, 'gi'));
                return regexpWords.reduce((acc, regexp) => acc + (regexp.test(`${event.id} ${event.name}`) ? 1 : 0), 0) === regexpWords.length;
            });

            //Case il allSelected is checked and filter has changed
            if (filtered.length > selectedEvents.length) {
                setAllSelected(false);
            }

            setFilteredEvents(filtered);
        },
        [events, selectedEvents]
    );

    const handleAllClick = React.useCallback(
        (event, data) => {
            setAllSelected(!allSelected);
            if (data.checked) {
                setSelectedEvents(filteredEvents.map(event => event.id));
            } else {
                setSelectedEvents([]);
            }
        },
        [allSelected, events, selectedEvents, filteredEvents]
    );

    const submit = React.useCallback(() => {
        return onSubmit(events.filter(e => selectedEvents.includes(e.id)));
    }, [onSubmit, selectedEvents]);

    // Set new filtered events if events changed
    React.useEffect(() => {
        setFilteredEvents(events);
    }, [open, events]);

    if (!events || !events.length) {
        return null;
    }

    return (
        <Modal
            size="large"
            open={open}
            onClose={() => {
                if (!isSubmitting) {
                    close();
                }
            }}
            title={t('ticket.rate_template.event_modal_title')}
            submitButton={<Button primary onClick={submit} disabled={isSubmitting} isLoading={isSubmitting} label={t('ticket.common.action.confirm')} />}
            cancelButton={<Button onClick={close} disabled={isSubmitting} label={t(`ticket.common.action.cancel`)} />}
            subtitle={t('ticket.rate_template.event_modal_subtitle')}
        >
            <div style={{ display: 'flex' }} className="rates-picker-input-container">
                <Search placeholder="Filtre" style={{ flex: '1' }} onChange={(event, value) => filterEvents(value)} handleReset={() => filterEvents('')} />
            </div>
            {selectedEvents.length > 0 && (
                <div>
                    {selectedEvents.length} {t('ticket.rate_template.event_modal_number_events', { count: selectedEvents.length })}
                </div>
            )}
            <Table useNew selectable onSelectAll={handleAllClick} basic celled>
                <Table.THead>
                    <Table.Tr isHeader active={allSelected}>
                        <th>{t('ticket.common.action.select-all')}</th>
                    </Table.Tr>
                </Table.THead>
                <Table.TBody>
                    {filteredEvents.map(event => (
                        <SelectableEvent
                            key={event.id}
                            event={event}
                            selectedEvents={selectedEvents}
                            onChange={({ target: { checked } }) => {
                                if (checked) {
                                    selectEvent(event);
                                } else {
                                    deselectEvent(event);
                                }
                            }}
                            label={formatLabel(event, t)}
                        />
                    ))}
                </Table.TBody>
            </Table>
        </Modal>
    );
}

EventsPickerModal.propDefaults = {
    open: false
};

EventsPickerModal.propTypes = {
    open: PropTypes.bool.isRequired,
    events: PropTypes.array.isRequired,
    onSubmit: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,
    formatLabel: PropTypes.func.isRequired
};
